import {FormInstance} from "antd";
import {useRouter} from "next/router";
import {withCache} from "@/base/utils/cache";
import {useReferral} from "@/base/utils/referral.hook";
import {SessionData} from "@/base/types/SessionData";
import {useComeFrom} from "@/base/utils/comefrom.hook";

export type FetchOptions = {
	body?: any,
	method?: string | null,
	params?: any,
	url: any,
	options?: any,
	session?: any,
	form?: FormInstance | null,
	locale?: string | null
}

export const FixJsonBody = (bodyN: any) => {
	const output: any = {}

	// replace to snakecase with -
	Object.keys(bodyN).forEach((key) => {
		const name = key.replace(/([A-Z])/g, "-$1").toLowerCase();
		output[name] = bodyN[key];
	})

	// remove all undefined
	Object.keys(output).forEach((key) => {
		if (output[key] === undefined)
		{
			delete output[key];
		}
	})

	return output;
}

// copy of fetch
export const Fetch = async ({url, method = 'GET', options = {}, body = null, locale = null, form = null, session = null, params = {}}: FetchOptions) => {
	const isJsonBody = body instanceof Object && !(body instanceof FormData);
	if (isJsonBody)
	{
		body = FixJsonBody(body);
	}

	// fix params
	if (params instanceof Object)
	{
		params = FixJsonBody(params);
	}

	const outputBody = body ? (isJsonBody ? JSON.stringify(body) : body) : options.body || null;
	const headers = {
		...options.headers || {},
		'Accept': 'application/json',
		'sp-site': process.env.NEXT_PUBLIC_SITE || 'default',
		// 'Content-Type': 'application/json',
		// Authorization: `Bearer ${token}`,

	};

	// if client add X-CSRF-Token
	if (typeof window !== 'undefined')
	{
		headers['X-CSRF-Token'] = await getCSRFToken()
	}

	if (session && session.access_token && !headers.hasOwnProperty('Authorization'))
	{
		headers['Authorization'] = `Bearer ${session.access_token}`
	}

	if (locale)
	{
		headers['Locale'] = locale;
	}

	if (outputBody && typeof outputBody == 'string' && (outputBody.startsWith("{") || outputBody.startsWith("[")))
	{
		headers['Content-Type'] = 'application/json';
	}

	if (params && Object.keys(params).length > 0)
	{
		url = addQueryParams(url, params);
	}

	const buildOptions = {
		...options,
		headers,
		body: outputBody,
		method: method
	};
	return fetch(
		url,
		buildOptions,
	).then(async (res) => {
		if (res.headers.get('content-type')?.toLowerCase()?.includes('application/json'))
		{
			const json = await res.json();

			if (json.hasOwnProperty('errors') && res.status !== 200)
			{
				json.message = (json.errors instanceof Object ? Object.values(json.errors) : json.errors).join("\n");
				throw json;
			}

			return json
		}

		// if not ok throw error
		if (!res.ok)
		{
			const text = await res.text();
			throw new Error(text);
		}

		return res.text();
	}).catch((e) => {
		// if (e.hasOwnProperty('errors'))
		// {
		// 	showErrors(e.errors, form);
		// }

		console.error(e)

		throw e;
	});
}

// useFetch return Fetch with default options
export const useFetch = (data?: SessionData, referer: string | null = null, comeFrom: string | null = null) => {
	const router = useRouter();

	const {getCurrentUsedCode, isUsingReferral} = useReferral();
	const {getCurrentComeFrom, isUsingComeFrom} = useComeFrom(comeFrom, referer);

	return [
		async (options: FetchOptions) => {
			return await Fetch({
				...options,
				options: {
					...options.options,
					headers: {
						...options.options?.headers || {},
						...(isUsingReferral() ? {'SP-Referral': getCurrentUsedCode()} : {}),
						...(referer ? {'SP-Referer': referer, 'sp-comefrom': 'referral'} : {
							...(isUsingComeFrom() ? {'sp-comefrom': getCurrentComeFrom()} : {})
						}),
					}
				},
				session: data,
				locale: router.locale
			});
		}
	]
}

export const getCSRFToken = async () => {
	return withCache(
		'user_csrf_token',
		// 5 minutes
		1000 * 60 * 5,
		() => fetch(ModifyURL('/user/csrf'), {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		}).then(async (res) => {
			return ((await res.json())?.data || {})?.csrf || undefined;
		}),
		true
	);
}

export const addQueryParams = (url: string, params: any = {}) => {
	const overallParams: Record<string, any> = {};
	let oUrl = url;

	try
	{
		const urlObj = new URL(url);
		const urlParams = new URLSearchParams(urlObj.search);
		urlParams.forEach((value, key) => {
			overallParams[key] = value;
		});

		oUrl = urlObj.origin + urlObj.pathname;
	}
	catch (e)
	{
		console.error(e);
	}


	const queryString = Object.entries({
		...overallParams,
		...params
	})
		.map(([key, value]) => {
			if (Array.isArray(value))
			{
				// Handle arrays by repeating the key for each array element
				return value.map(item => `${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`).join('&');
			}
			else if (value instanceof Object)
			{
				// Handle objects by encoding each key and value, then repeating the key for each object property
				return Object.entries(value).map(([k, v]) => `${encodeURIComponent(key)}[${encodeURIComponent(k)}]=${encodeURIComponent(v)}`).join('&');
			}
			else if (value)
			{
				return `${encodeURIComponent(key)}=${encodeURIComponent(value?.toString())}`;
			}
		})
		.join('&');

	// Check if the original URL already contains a query string
	const separator = oUrl.includes('?') ? '&' : '?';

	// Combine the original URL and the new query string
	if (queryString.length > 0)
	{
		return oUrl + separator + queryString;
	}

	return url;
}

export const ModifyURL = (url: string, queryParams: any = {}) => {
	if (url.startsWith('http'))
	{
		return addQueryParams(url, queryParams);
	}
	else
	{
		const apiUrl = (process.env.INTERNAL_API_URL && typeof window == 'undefined') ?
			(process.env.INTERNAL_API_URL || '').toString() :
			(process.env.NEXT_PUBLIC_API_URL || '').toString();

		if (apiUrl.endsWith('/'))
		{
			apiUrl.slice(0, -1);
		}

		if (url.startsWith('/'))
		{
			url = url.slice(1);
		}

		return addQueryParams(`${apiUrl}/${url}`, queryParams);
	}
}